import { COLLECTION_MOCKUP, IMAGE_ORIGIN } from '../data/collection'
import { FACEBOOK_DOMAIN_VERIFICATION } from '../data/analytics'

interface MetaTag {
  /** `name` for standard/Twitter tags, `property` for Open Graph. */
  attr: 'name' | 'property'
  key: string
  content: string
}

interface LinkTag {
  rel: string
  href: string
  as?: string
  crossOrigin?: string
  fetchPriority?: 'high' | 'low' | 'auto'
}

const PAGE_TITLE = 'Pequeñas Puntadas™ · 310 moldes de ropa infantil para imprimir'
const PAGE_DESCRIPTION = '31 modelos en 10 talles, de recién nacido a 4 años. Moldes A4 + manuales paso a paso.'

const SHARE_IMAGE = COLLECTION_MOCKUP

const META_TAGS: MetaTag[] = [
  { attr: 'name', key: 'title', content: PAGE_TITLE },
  { attr: 'name', key: 'description', content: PAGE_DESCRIPTION },
  { attr: 'property', key: 'og:type', content: 'website' },
  { attr: 'property', key: 'og:site_name', content: 'Pequeñas Puntadas™' },
  { attr: 'property', key: 'og:title', content: PAGE_TITLE },
  { attr: 'property', key: 'og:description', content: PAGE_DESCRIPTION },
  { attr: 'property', key: 'og:locale', content: 'es_AR' },
  { attr: 'name', key: 'twitter:card', content: 'summary_large_image' },
  { attr: 'name', key: 'twitter:title', content: PAGE_TITLE },
  { attr: 'name', key: 'twitter:description', content: PAGE_DESCRIPTION },
  ...(SHARE_IMAGE.src
    ? [
        { attr: 'property' as const, key: 'og:image', content: SHARE_IMAGE.src },
        { attr: 'property' as const, key: 'og:image:secure_url', content: SHARE_IMAGE.src },
        { attr: 'property' as const, key: 'og:image:type', content: 'image/webp' },
        { attr: 'property' as const, key: 'og:image:width', content: String(SHARE_IMAGE.width) },
        { attr: 'property' as const, key: 'og:image:height', content: String(SHARE_IMAGE.height) },
        { attr: 'property' as const, key: 'og:image:alt', content: SHARE_IMAGE.alt },
        { attr: 'name' as const, key: 'twitter:image', content: SHARE_IMAGE.src },
        { attr: 'name' as const, key: 'twitter:image:alt', content: SHARE_IMAGE.alt },
      ]
    : []),
  ...(FACEBOOK_DOMAIN_VERIFICATION
    ? [{ attr: 'name' as const, key: 'facebook-domain-verification', content: FACEBOOK_DOMAIN_VERIFICATION }]
    : []),
]

const LINK_TAGS: LinkTag[] = [
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
  { rel: 'preconnect', href: IMAGE_ORIGIN },
  // The hero mockup is the LCP image.
  ...(SHARE_IMAGE.src ? [{ rel: 'preload', href: SHARE_IMAGE.src, as: 'image', fetchPriority: 'high' as const }] : []),
]

/** Inserts global <meta> and resource-hint <link> tags into <head> once. */
export function initHeadMeta(): void {
  if (typeof document === 'undefined') return

  document.title = PAGE_TITLE
  document.documentElement.lang = 'es-AR'

  // Remove platform-injected variants with the wrong attribute (e.g. name="og:title"
  // or property="twitter:title") so no stale title/description remains.
  document
    .querySelectorAll(
      [
        'meta[name="og:title"]',
        'meta[name="og:description"]',
        'meta[property="twitter:title"]',
        'meta[property="twitter:description"]',
        'meta[property="description"]',
        'meta[name="og:image"]',
        'meta[property="twitter:image"]',
        'meta[property="twitter:card"]',
      ].join(', '),
    )
    .forEach((node) => node.remove())

  // Exactly one viewport tag, at device width and initial scale 1 (zoom stays allowed).
  const viewports = Array.from(document.querySelectorAll<HTMLMetaElement>('meta[name="viewport"]'))
  const viewport = viewports[0] ?? document.createElement('meta')
  viewports.slice(1).forEach((node) => node.remove())
  viewport.name = 'viewport'
  viewport.content = 'width=device-width, initial-scale=1'
  if (!viewport.parentNode) document.head.prepend(viewport)
  if (!FACEBOOK_DOMAIN_VERIFICATION) {
    document.querySelector('meta[name="facebook-domain-verification"]')?.remove()
  }
  // Remove stale image tags from the previous product.
  if (!SHARE_IMAGE.src) {
    document
      .querySelectorAll('meta[property="og:image"], meta[property="og:image:alt"], meta[name="twitter:image"]')
      .forEach((node) => node.remove())
  }
  document.querySelectorAll('script[type="application/ld+json"]').forEach((node) => node.remove())

  META_TAGS.forEach(({ attr, key, content }) => {
    const existing = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
    if (existing) {
      existing.content = content
      return
    }
    const meta = document.createElement('meta')
    meta.setAttribute(attr, key)
    meta.content = content
    document.head.appendChild(meta)
  })

  LINK_TAGS.forEach(({ rel, href, as, crossOrigin, fetchPriority }) => {
    if (document.querySelector(`link[rel="${rel}"][href="${href}"]`)) return
    const link = document.createElement('link')
    link.rel = rel
    link.href = href
    if (as) link.as = as
    if (crossOrigin) link.crossOrigin = crossOrigin
    if (fetchPriority) link.setAttribute('fetchpriority', fetchPriority)
    document.head.appendChild(link)
  })
}
